import { FC, useMemo } from "react";
import { Alert, Button, Skeleton } from "react-daisyui";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuthContext } from "@/context/AuthContext.tsx";
import Routes from "@/router/Routes.ts";
import NewTripButton from "@/components/functional/NewTripButton/NewTripButton.tsx";
import useItineraries from "@/api/itineraries/useItineraries.ts";
import useTrips from "@/api/trips/useTrips.ts";
import TripBlockRich from "@/components/functional/TripBlockRich/TripBlockRich.tsx";
import useStorage from "@/api/useStorage.ts";
import StorageBucket from "$/_const/StorageBucket.ts";
import dayjs from "$/utils/dayjs";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import { passToRoute } from "@/utils/passToRoute.ts";
import tripListItineraries, { pairWithTrips } from "@/utils/trip/tripListItineraries.ts";
import useRetryTripGeneration from "@/hooks/useRetryTripGeneration.ts";

const isRemoteUrl = (value: string): boolean =>
  value.startsWith("https://") || value.startsWith("http://");

const HomePage: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const {
    data: itineraries,
    isLoading: isLoadingItineraries,
    error: errorLoadingItineraries,
  } = useItineraries(!!user, {
    user_id: user?.id,
  });
  const {
    data: trips,
    isLoading: isLoadingTrips,
    error: errorLoadingTrips,
  } = useTrips(!!user?.id, {
    user_id: user?.id,
  });

  const { getPublicUrl } = useStorage(StorageBucket.TRIP_IMAGES);

  const { ready: visibleItineraries, failed: failedItineraries } = useMemo(
    () => tripListItineraries(itineraries ?? [], Date.now()),
    [itineraries],
  );

  const failedTrips = useMemo(
    () => pairWithTrips(failedItineraries, trips),
    [failedItineraries, trips],
  );

  const { retry, retryingTripId } = useRetryTripGeneration();

  const itineraryTripIds = useMemo(
    () => new Set(itineraries?.map((itinerary) => itinerary.trip_id) ?? []),
    [itineraries],
  );

  const draftTrips = useMemo(
    () =>
      trips?.filter(
        (trip) =>
          !itineraryTripIds.has(trip.id) &&
          Boolean(trip.data?.trip_destination),
      ) ?? [],
    [itineraryTripIds, trips],
  );

  const latestTripName =
    draftTrips[0]?.data?.trip_destination ??
    visibleItineraries[0]?.data?.name ??
    null;
  const isLoadingTripsList = isLoadingItineraries || isLoadingTrips;
  const hasVisibleTrips =
    draftTrips.length > 0 ||
    failedTrips.length > 0 ||
    visibleItineraries.length > 0;
  const preferredName = user?.preferred_name ?? "";

  const greeting = useMemo(() => {
    const hour = new Date().getHours();

    if (hour < 12) {
      return t("tripNavbar.good_morning", {
        preferred_name: preferredName,
        defaultValue: "Good morning, {{preferred_name}}",
      });
    }

    if (hour < 18) {
      return t("tripNavbar.good_afternoon", {
        preferred_name: preferredName,
        defaultValue: "Good afternoon, {{preferred_name}}",
      });
    }

    return t("tripNavbar.good_evening", {
      preferred_name: preferredName,
      defaultValue: "Good evening, {{preferred_name}}",
    });
  }, [preferredName, t]);

  return (
    <div className="flex min-h-[100dvh] w-full min-w-0 flex-col overflow-y-auto bg-journi-theme-light text-base-content">
      <header className="w-full px-5 pt-4 md:px-10 lg:px-12">
        <div className="flex w-full items-center gap-4 border-b border-primary/40 pb-4">
          <TripLayout.OpenMenuButton
            color="ghost"
            className="!h-9 !min-h-9 !w-9 !p-2 border-2 border-primary text-primary hover:bg-primary/10"
          />

          <Link
            to={Routes.DASHBOARD}
            className="font-feature text-3xl font-semibold tracking-wide text-base-content"
          >
            JOURNI
          </Link>
        </div>
      </header>

      <main className="flex w-full min-w-0 grow flex-col px-5 pb-10 pt-8 md:px-10 lg:px-12">
        <section className="w-full max-w-[900px]">
          <p className="font-sans text-base font-medium text-[#80691F]">
            {greeting}
          </p>

          <h1 className="mt-2 font-feature text-4xl font-semibold leading-tight text-base-content">
            {t("tripNavbar.where_to", { defaultValue: "Where to" })}{" "}
            <span className="italic text-primary">
              {t("tripNavbar.next", { defaultValue: "next?" })}
            </span>
          </h1>

          <p className="mt-3 max-w-[780px] whitespace-pre-line font-sans text-base leading-6 text-text-secondary">
            {latestTripName
              ? t("tripNavbar.last_time", {
                  trip: latestTripName,
                })
              : t("tripNavbar.home_intro", {
                  defaultValue:
                    "Your Travel DNA is ready. Tell Cody where you would like to go next.",
                })}
          </p>

          <div className="mt-10 w-full max-w-[220px]">
            <NewTripButton variant="dashboard" className="!min-h-[44px]" />
          </div>
        </section>

        <section className="mt-12 w-full min-w-0">
          <h2 className="font-sans text-sm font-medium uppercase text-text-secondary">
            {t("tripNavbar.your_trips")}
          </h2>

          {!isLoadingTripsList && errorLoadingItineraries ? (
            <Alert status="error" className="mt-4">
              {errorLoadingItineraries.message}
            </Alert>
          ) : null}
          {!isLoadingTripsList && errorLoadingTrips ? (
            <Alert status="error" className="mt-4">
              {errorLoadingTrips.message}
            </Alert>
          ) : null}

          <div className="mt-4 w-full overflow-x-auto pb-5 [scrollbar-color:rgba(31,41,55,0.2)_transparent] [scrollbar-width:thin]">
            {isLoadingTripsList ? (
              <div className="flex min-w-max gap-8">
                {[0, 1, 2].map((index) => (
                  <Skeleton
                    key={index}
                    className="h-[250px] w-[340px] shrink-0 rounded-xl"
                  />
                ))}
              </div>
            ) : hasVisibleTrips ? (
              <div className="flex min-w-max gap-8 pr-10">
                {draftTrips.map((trip) => (
                  <TripBlockRich
                    key={`draft-${trip.id}`}
                    className="w-[min(84vw,340px)] shrink-0"
                    id={trip.id}
                    name={trip.data.trip_destination!}
                    arrivalDate={trip.data.arrival_date}
                    departureDate={trip.data.departure_date}
                    duration={trip.data.duration}
                    statusLabel={t("tripNavbar.draft", {
                      defaultValue: "Draft",
                    })}
                    href={passToRoute(Routes.TRIP__CONFIRM, {
                      id: trip.id,
                    })}
                  />
                ))}
                {failedTrips.map(({ itinerary, trip, name }) => (
                  <TripBlockRich
                    key={`failed-${itinerary.trip_id}`}
                    className="w-[min(84vw,340px)] shrink-0"
                    id={itinerary.trip_id}
                    name={name}
                    arrivalDate={trip.data.arrival_date}
                    departureDate={trip.data.departure_date}
                    duration={trip.data.duration}
                    statusLabel={t("tripList.failed_badge")}
                    statusTone="error"
                    action={
                      <Button
                        size="sm"
                        color="primary"
                        loading={retryingTripId === itinerary.trip_id}
                        disabled={retryingTripId !== null}
                        onClick={() => void retry(itinerary.trip_id)}
                      >
                        {t("button.retry")}
                      </Button>
                    }
                  />
                ))}
                {visibleItineraries.map((itinerary) => {
                  const imageUrl = itinerary.image
                    ? isRemoteUrl(itinerary.image)
                      ? itinerary.image
                      : `${getPublicUrl(itinerary.image)}?cache=${dayjs(
                          itinerary.updated_at,
                        ).format("YYYYMMDDHHmmss")}`
                    : undefined;

                  return (
                    <TripBlockRich
                      key={itinerary.trip_id}
                      className="w-[min(84vw,340px)] shrink-0"
                      id={itinerary.trip_id}
                      name={itinerary.data!.name}
                      arrivalDate={itinerary.data!.arrival_date}
                      departureDate={itinerary.data!.departure_date}
                      duration={itinerary.data!.per_day_plan.length}
                      src={imageUrl}
                      title={itinerary.data!.description}
                      tags={itinerary.data!.tags}
                      photoCredit={itinerary.image_provider === "unsplash" &&
                          itinerary.image_photographer_name
                        ? {
                          name: itinerary.image_photographer_name,
                          profileUrl: itinerary.image_photographer_url,
                          sourceUrl: itinerary.image_source_url,
                        }
                        : undefined}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-primary/60 px-6 py-8 text-center font-sans text-text-secondary">
                {t("tripNavbar.no_trips_yet", {
                  defaultValue: "Your trips will appear here.",
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;